import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ExclamationCircleIcon } from '@heroicons/react/24/outline';
import * as Yup from 'yup';

import { useYupValidationResolver } from 'hooks/useYupValidationResolver/useYupValidationResolver';
import { useSsccConfiguration, useUpdateSsccConfiguration } from 'services/repositories/sscc/sscc';
import { NotificationType, useNotificationStore } from 'stores/notifications/notifications';
import { processSubmissionErrors } from 'support/helpers/errors/errors';
import { EmptyState } from 'components/EmptyState/EmptyState';
import { Button } from 'components/Form/Button/Button';
import { Form } from 'components/Form/Form';
import { ValidationErrors } from 'components/Form/ValidationErrors/ValidationErrors';
import { LoadingPage } from 'components/Loading/LoadingPage';
import { UnsavedChangesModal } from 'components/Navigation/UnsavedChangesModal/UnsavedChangesModal';
import { Page } from 'components/Page/Page';

import { SettingsSidebar } from '../SettingsSidebar/SettingsSidebar';
import { GenerationSection } from './components/GenerationSection';
import { LabelsSection } from './components/LabelsSection';
import { RequirementSection } from './components/RequirementSection';
import {
  buildSsccUpdatePayload,
  canSeedCounter,
  DEFAULT_SSCC_SETTINGS_FORM_VALUES,
  getSsccOwnedSections,
  getSsccSettingsFormValues,
  GS1_PREFIX_PATTERN,
  isValidFirstSerialNumber,
  SsccSettingsFormState,
} from './helpers';

const FORM_ID = 'ssccSettings';

export const SsccSettings = () => {
  const { t } = useTranslation();
  const { addNotification } = useNotificationStore();
  const { data: configuration, isLoading, isError, refetch } = useSsccConfiguration();
  const { mutate, isLoading: isSaving } = useUpdateSsccConfiguration();
  const [nonInputErrors, setNonInputErrors] = useState<Array<string>>([]);

  const { generation, label, requirement } = configuration ?? {};
  const labelOnly = Boolean(label) && !generation;
  // A label-only 3PL must pick a resolution or its save would be empty; a buyer-3PL can save its unit alone.
  const dpmmRequired = labelOnly && !requirement;

  const formSchema = Yup.object({
    ...(!generation
      ? {}
      : {
          generation: Yup.object({
            gs1_prefix: Yup.string()
              .required(t('settings:ssccSettings.form.gs1PrefixValidation') ?? '')
              .matches(GS1_PREFIX_PATTERN, t('settings:ssccSettings.form.gs1PrefixValidation') ?? ''),
            counter_seed: Yup.string().test(
              'first-serial-number',
              t('settings:ssccSettings.form.firstSerialNumberValidation') ?? '',
              isValidFirstSerialNumber,
            ),
          }),
        }),
    label: Yup.object({
      dpmm: dpmmRequired ? Yup.number().required(t('settings:ssccSettings.form.dpmmValidation') ?? '') : Yup.number(),
    }),
  });

  const resolver = useYupValidationResolver(formSchema);
  const {
    control,
    register,
    handleSubmit,
    reset,
    setError,
    watch,
    formState: { errors, isDirty },
  } = useForm<SsccSettingsFormState>({
    resolver,
    values: getSsccSettingsFormValues(configuration),
  });

  const seedable = canSeedCounter(generation?.gs1_company_prefix ?? null, watch('generation.gs1_prefix'));

  if (isLoading) {
    return <LoadingPage />;
  }

  if (isError || !configuration) {
    return (
      <Page>
        <Page.Head title={t('common:mainMenu.settings')} />
        <Page.Section leftAside={<SettingsSidebar />}>
          <EmptyState
            icon={<ExclamationCircleIcon />}
            title={t('settings:ssccSettings.error.title')}
            description={t('settings:ssccSettings.error.description') ?? undefined}
            ctaText={t('settings:ssccSettings.error.retry') ?? undefined}
            ctaOnClick={() => refetch()}
          />
        </Page.Section>
      </Page>
    );
  }

  const onSubmit = (submission: SsccSettingsFormState) => {
    setNonInputErrors([]);

    mutate(buildSsccUpdatePayload(submission, { owned: getSsccOwnedSections(configuration), seedable }), {
      onSuccess: () => {
        addNotification(t('settings:ssccSettings.onSuccess'), NotificationType.success);
      },
      onError: (error) => {
        processSubmissionErrors<SsccSettingsFormState>({
          error,
          setInputError: setError,
          setNonInputErrors,
          defaultData: DEFAULT_SSCC_SETTINGS_FORM_VALUES,
          t,
        });
      },
    });
  };

  return (
    <>
      <Page>
        <Page.Head title={t('common:mainMenu.settings')} />

        <Page.Section leftAside={<SettingsSidebar />}>
          <Form
            onSubmit={handleSubmit(onSubmit)}
            id={FORM_ID}
            data-testid={FORM_ID}
            className="w-full divide-y divide-gray-200 *:py-6 *:first:pt-0 *:last:pb-0"
          >
            <ValidationErrors errors={nonInputErrors} />

            {generation && (
              <GenerationSection
                control={control}
                register={register}
                errors={errors.generation}
                seedable={seedable}
                counterValue={generation.counter_value}
                disabled={isSaving}
              />
            )}

            {label && (
              <LabelsSection
                control={control}
                labelOnly={labelOnly}
                required={dpmmRequired}
                errors={errors.label}
                disabled={isSaving}
              />
            )}

            {requirement && (
              <RequirementSection
                control={control}
                unitOfMeasureOptions={requirement.unit_of_measure_options}
                errors={errors.requirement}
                disabled={isSaving}
              />
            )}

            <div>
              <Button
                className="mr-3"
                variant="secondary"
                onClick={() => reset()}
                disabled={isSaving}
                analyticsId="sscc-settings:update-cancel"
              >
                {t('common:cancel')}
              </Button>
              <Button type="submit" form={FORM_ID} loading={isSaving} analyticsId="sscc-settings:update_save">
                {t('common:save')}
              </Button>
            </div>
          </Form>
        </Page.Section>
      </Page>

      <UnsavedChangesModal shouldBlock={isDirty} />
    </>
  );
};
